/**
 * Shared chrome for the learner screens — masthead, breadcrumb, progress bar,
 * and the icon set the comp uses.
 *
 * Everything here is presentational and takes its data as props. Nothing
 * imports a fixture: that is what lets `/demo` and the real `/courses` mount
 * the same components with different sources.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

export type LearnerNavKey = 'home' | 'catalog' | 'progress' | 'ce';

/**
 * The learner's whole navigation, and the two rules it encodes.
 *
 * ONE ENTRY PER DESTINATION. There used to be a second built item, "My
 * Courses", and it went exactly where Home goes: every call site read
 * `key === 'home' || key === 'courses'` and navigated to `/courses`, which
 * renders the same component either way. Two names for one place is not a
 * richer bar, it is a bar that makes a learner wonder which one they wanted —
 * and it had spread, into the course overview's breadcrumb, as a second crumb
 * pointing at the first one's handler. Home IS the course list.
 *
 * `built: false` renders the item disabled with a visible "soon" chip — never
 * as an enabled-looking no-op. UX review rule, and the demo's core honesty
 * property: for a first-time user, silence after a click reads as *broken*,
 * not *unbuilt*, and the masthead is the first place a lost user reaches.
 * The chip is visible microcopy, not a title tooltip — tooltips are invisible
 * on touch and undiscoverable to the 65-year-old this product is built for.
 */
const NAV: { key: LearnerNavKey; label: string; built: boolean }[] = [
  { key: 'home', label: 'Home', built: true },
  { key: 'catalog', label: 'Catalog', built: false },
  { key: 'progress', label: 'Progress', built: false },
  { key: 'ce', label: 'CE Credits', built: false },
];

/**
 * THE NAV, OFFERED TO WHATEVER IS IN THE ACCOUNT SLOT.
 *
 * Below 860px the bar cannot hold four items, a course title and an avatar on
 * one row, so `.l-mast-nav` is not drawn — and until now nothing replaced it,
 * which made "the same navigation on every screen" true of desktop only. The
 * account menu is where it goes (Pete, 2026-08-27): that menu already exists,
 * already opens from the one control that IS on the bar at every width, and
 * already holds the other things that used to crowd it.
 *
 * A CONTEXT RATHER THAN A PROP, because `actions` is a slot: the screens hand
 * `Masthead` an opaque node and three different containers construct it, one of
 * which (the player) derives its nav handler inside the screen itself. Threading
 * a prop would mean touching all of them to deliver something only the menu
 * reads. The slot is rendered INSIDE this header, so a provider here is always
 * an ancestor of whatever fills it.
 *
 * `null` outside a learner masthead — the console and admin mastheads have no
 * learner nav, and a menu that finds nothing here simply renders none.
 *
 * The provider wraps the whole `actions` subtree rather than one component, so
 * strictly it offers this nav to ANY `AccountMenu` rendered inside it. That is
 * the cost of a slot: the header cannot see what fills it. Nested mastheads
 * shadow correctly (the inner provider wins), and no surface composes two
 * learner mastheads today — but "the menu gets its own masthead's nav" is a
 * convention here, not something the types enforce.
 */
export interface LearnerNav {
  items: readonly { key: LearnerNavKey; label: string; built: boolean }[];
  current?: LearnerNavKey;
  onNavigate?: (key: LearnerNavKey) => void;
}

const LearnerNavContext = createContext<LearnerNav | null>(null);

export function useLearnerNav(): LearnerNav | null {
  return useContext(LearnerNavContext);
}

export interface Institution {
  mark: string;
  name: string;
  unit: string;
}

export function Masthead({
  current,
  onNavigate,
  actions,
  children,
}: {
  /**
   * The nav item this screen IS — never merely the one it sits under.
   *
   * `aria-current="page"` is a claim of identity: it means "this control's
   * target is the page you are on". The player and the course overview are
   * reached THROUGH Home and are not Home, so both leave this undefined.
   * Setting it there would tell a screen-reader user they were on the course
   * list while they sat inside lecture 5, and `.l-mast-nav button[aria-current]`
   * draws a gold underline, so it tells sighted users the same thing. Those
   * screens say where they are with `children` and with the rail's own
   * `aria-current`, which are about the right things.
   */
  current?: LearnerNavKey;
  onNavigate?: (key: LearnerNavKey) => void;
  /**
   * Account-level controls — theme toggle, session menu. A slot rather than a
   * fixed block because the walkthrough has no session to control and must not
   * grow a sign-out button that signs nobody out, while the real screens carry
   * their OWN chrome (they hang outside `AppShell`) and would otherwise be the
   * only signed-in surface with no way to switch theme or leave.
   */
  actions?: ReactNode;
  /**
   * THE ONE ANCESTOR THE NAV CANNOT NAME.
   *
   * This slot used to be an either/or with the nav — a `compact` flag swapped
   * the whole `<nav>` out for it, and the player was the one caller that took
   * that deal, spending the space on prev/next lecture buttons. So walking into
   * a lecture made the site's navigation disappear, and the lecture rail twelve
   * pixels below could already reach every lecture rather than only two.
   *
   * Now both render, in this order: nav first, so the bar starts identically on
   * every screen and the underline is always in the same place; context second.
   * Home and the course overview pass nothing, because their ancestor is Home
   * and Home is already in the nav. The player passes its course.
   *
   * Whatever goes here MUST NOT carry an auto margin. The account block's
   * `margin-left: auto` is the only one in this row and is what pushes the
   * avatar to the edge; a second one splits the slack between the two instead
   * of pushing either anywhere. Yield with `flex: 0 1 auto; min-width: 0` — see
   * `.l-course-link` — rather than by claiming space.
   */
  children?: ReactNode;
}) {
  const nav = useMemo<LearnerNav>(
    () => ({ items: NAV, current, onNavigate }),
    [current, onNavigate],
  );

  return (
    <header className="l-mast">
      <button
        type="button"
        className="brand"
        onClick={() => onNavigate?.('home')}
        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
      >
        CU<em>S</em>P
      </button>

      <nav className="l-mast-nav" aria-label="Learner">
        {NAV.map((item) => (
          <button
            key={item.key}
            type="button"
            aria-current={current === item.key ? 'page' : undefined}
            disabled={!item.built}
            onClick={item.built ? () => onNavigate?.(item.key) : undefined}
          >
            {item.label}
            {!item.built && <span className="l-soon">soon</span>}
          </button>
        ))}
      </nav>

      {children}

      {/* The right side is a single element — the account card supplied through
          `actions`. The institution block and the decorative avatar that used
          to sit here folded into that card's menu (see `AccountMenu`), and
          below 860px the nav above folds into it too (see `LearnerNavContext`). */}
      {actions !== undefined && (
        <span className="l-mast-actions l-mast-actions-end">
          <LearnerNavContext.Provider value={nav}>{actions}</LearnerNavContext.Provider>
        </span>
      )}
    </header>
  );
}

export function Breadcrumb({
  trail,
  current,
}: {
  trail: { label: string; onClick: () => void }[];
  current: string;
}) {
  return (
    <nav className="l-crumb" aria-label="Breadcrumb">
      {trail.map((step) => (
        <span key={step.label} style={{ display: 'contents' }}>
          <button type="button" onClick={step.onClick}>
            {step.label}
          </button>
          <span aria-hidden="true" style={{ opacity: 0.5 }}>
            /
          </span>
        </span>
      ))}
      <b>{current}</b>
    </nav>
  );
}

/**
 * The progress bar. `pct` is the server's number, never recomputed here —
 * completion is watch-weighted and modality-agnostic, and a client that
 * derives it from a lecture count disagrees with the transcript.
 */
export function ProgressBar({ pct, label }: { pct: number; label?: string }) {
  return (
    <div
      className="l-prog"
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? 'Progress'}
    >
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * The one sanctioned voice for "this isn't built yet" — a visible muted line
 * beside the disabled control it explains. Every unbuilt affordance uses this
 * instead of a `title` tooltip, so the reason reaches touch devices and users
 * who never hover. Hidden wholesale in recording mode via CSS.
 */
export function Unbuilt({ children }: { children: ReactNode }) {
  return <p className="l-unbuilt">{children}</p>;
}

/** `504000` → `08:24`. Durations are seconds on the wire, positions are ms. */
export function formatMs(ms: number): string {
  return formatSeconds(Math.floor(ms / 1000));
}

export function formatSeconds(total: number): string {
  /* FLOORED, and not merely defensively. Catalog durations are integers on the
     wire, but a real media element reports its own duration as a float — a
     24-second clip is 23.85717 — and the unguarded arithmetic rendered that as
     `00:23.85717`. Seen in the browser the day playback went live. */
  const whole = Math.max(0, Math.floor(total));
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** `1330` → `22m`, `13300` → `3h 41m`, `24` → `24s`. At-a-glance length. */
export function formatDuration(totalSeconds: number): string {
  const whole = Math.max(0, Math.round(totalSeconds));
  /* SECONDS UNDER A MINUTE, because rounding to minutes here does not merely
     lose precision — it inverts the meaning. This renders "≈ {x} remaining" on
     the course screen, and a learner with twenty-four seconds left was told
     "0m remaining", which reads as "nothing left" on a lecture they have not
     finished. Short lectures are legitimate content, not only a demo artefact. */
  if (whole < 60) return `${whole}s`;
  const h = Math.floor(whole / 3600);
  const m = Math.round((whole % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

/* ── icons ────────────────────────────────────────────────────────────── */

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function PlayIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

export function PauseIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
    </svg>
  );
}

/**
 * The one indeterminate spinner in the learner surface — playback is being
 * licensed, an interval nothing can predict a length for. Everything else here
 * states a reason in words instead, and should keep doing so.
 *
 * The animation lives in CSS (`.l-spin`) rather than SMIL, so a user who has
 * asked for reduced motion actually gets it.
 */
export function SpinnerIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...stroke}
      className="l-spin"
      aria-hidden="true"
    >
      <path d="M12 3a9 9 0 1 0 9 9" />
    </svg>
  );
}

/*
 * `data-icon` ON THE RAIL DOT'S TWO GLYPHS. The dot is `aria-hidden` — it
 * repeats in colour and shape what the row already says in words — so there is
 * no accessible name to assert against, and both glyphs are anonymous
 * `<svg>`s. Without a marker, "only the first locked lecture shows a padlock"
 * is untestable: any test would be counting SVGs, and would pass just as
 * happily if the padlock were replaced by the tick. Named here rather than in
 * the test so the two cannot drift.
 */
export function LockIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...stroke}
      aria-hidden="true"
      data-icon="lock"
    >
      <rect x="4" y="11" width="16" height="9" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export function CheckIcon({ size = 9 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...stroke}
      strokeWidth={3}
      aria-hidden="true"
      data-icon="check"
    >
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

export function ShieldIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <rect x="3" y="11" width="18" height="10" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

export function VerifiedIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...stroke}
      stroke="var(--color-accent)"
      aria-label="Verified practitioner"
      role="img"
    >
      <path d="m9 12 2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
    </svg>
  );
}

export function PlusIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/* ── the lecture's three stages ───────────────────────────────────────────
   Lesson, Quiz and Mastery, as the comp draws them: three BOXED glyphs of one
   family, not three loose marks. The rail lists them as peers, and a bare
   triangle beside a bare tick beside a bare plus reads as three unrelated
   things — the plus especially, which everywhere else on this screen means
   "add". Enclosing each in the same 24-box makes them one set, and lets the
   pencil (an outline, not a mark) sit level with the other two.

   Sized like the rest of the stroked icons and drawn on the same 24 grid, so
   `size` is the only thing a caller ever chooses. */

/** Lesson — a play head inside its frame. The video stage. */
export function LessonIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      {/* Filled, unlike its frame: the play head is a solid mark at 11px, and
          a stroked triangle that small closes into a blob. */}
      <path d="M10.5 9.5v5l4-2.5z" fill="currentColor" />
    </svg>
  );
}

/** Quiz — a ticked box. Answering, not the "done" the rail's dots mean. */
export function QuizIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </svg>
  );
}

/** Mastery — a pencil. The one stage whose answer the learner WRITES. */
export function MasteryIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4z" />
      <path d="M14.5 5.5 18.5 9.5" />
    </svg>
  );
}

export function AlertIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...stroke}
      stroke="var(--color-accent)"
      aria-hidden="true"
      style={{ flex: 'none', marginTop: 1 }}
    >
      <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}

export function ClockIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...stroke}
      aria-hidden="true"
      style={{ flex: 'none', marginTop: 1, opacity: 0.5 }}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function HeadphonesIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <path d="M3 14v-3a9 9 0 0 1 18 0v3" />
      <path d="M21 16a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 2zM3 16a2 2 0 0 0 2 2h1v-6H5a2 2 0 0 0-2 2z" />
    </svg>
  );
}

/** An open book — the syllabus, wherever it is reachable from. */
export function SyllabusIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <path d="M2 4h6a4 4 0 0 1 4 4v12a3 3 0 0 0-3-3H2zM22 4h-6a4 4 0 0 0-4 4v12a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

export function BookmarkIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z" />
    </svg>
  );
}

export function CcIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" />
      <path d="M6 12h2m3 0h5" />
    </svg>
  );
}

export function FullscreenIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
    </svg>
  );
}

export function TheaterIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke} aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" />
      <rect x="5" y="9" width="14" height="6" />
    </svg>
  );
}
